import { MessageModule } from 'primeng/message';
import { ToastModule } from 'primeng/toast';
import { BrowserModule } from '@angular/platform-browser';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { LOCALE_ID, isDevMode, APP_INITIALIZER, NgModule, ErrorHandler } from '@angular/core';
import '@angular/common/locales/global/pl'
import { KeycloakAngularModule, KeycloakService, KeycloakBearerInterceptor } from 'keycloak-angular';
import { encodeApiParam } from './common/api-param-encoder';
import { environment } from 'src/environments/environment';
import { ServiceWorkerModule } from '@angular/service-worker';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { GoogleTagManagerModule } from 'angular-google-tag-manager';
import { DateInterceptor } from './services/bdoservice/common/interceptors/date-interceptor';
import { DashboardModule } from './dashboard/dashboard.module';
import { Router } from "@angular/router";
import * as Sentry from "@sentry/angular-ivy";
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { companyContextReducer, localStorageMetaReducer } from './common/ui/company-context-select/company-context.reducer';
import { ApiModule as PackagingApiModule, Configuration as PackagingConfiguration, ConfigurationParameters as PackagingConfigurationParameters } from '@mobiletract/packaging-management-service-angular-client';
import { ApiModule as WasteRecordApiModule, Configuration as WasteRecordConfiguration, ConfigurationParameters as WasteRecordConfigurationParameters } from '@mobiletract/wasterecord-service-angular-client';
import { ApiModule as AccountServiceApiModule, Configuration as AccountServiceConfiguration, ConfigurationParameters as AccountServiceConfigurationParameters } from '@mobiletract/account-service-angular-client';
import { ApiModule as AuthServiceApiModule, Configuration as AuthServiceConfiguration, ConfigurationParameters as AuthServiceConfigurationParameters } from '@mobiletract/auth-service-angular-client';
import { ApiModule as ReportingServiceApiModule, Configuration as ReportingServiceConfiguration, ConfigurationParameters as ReportingServiceConfigurationParameters } from '@mobiletract/reporting-service-angular-client';

import { HttpClientModule } from '@angular/common/http';
import { MultiTranslateHttpLoader } from '@ekowitryna/ui';
import { version } from '../../package.json';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { Error404Module } from './error-page/error-page.module';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { providePrimeNG } from 'primeng/config';
import { ekowitrynaPreset, provideEkoHostAppMetadata } from '@ekowitryna/ui';
import packageJson from '../../package.json';
import { DialogService } from 'primeng/dynamicdialog';
import { provideEkoPreferencesSync } from './core/providers/eko-dark-mode.providers';
import { provideEkoBrowserTitle } from '@ekowitryna/ui';

export function HttpLoaderFactory(http: HttpClient) {
  return new MultiTranslateHttpLoader(http, [
    { prefix: './assets/i18n/', suffix: '.json?version=' + version },
    { prefix: './assets/ui/i18n/', suffix: '.json' }
  ]);
}



function initializeKeycloak(keycloak: KeycloakService) {
  return () =>
    keycloak.init({
      config: {
        url: environment.keycloakUrl,
        realm: environment.realm,
        clientId: environment.clientId
      },
      initOptions: {
        onLoad: 'check-sso',
        checkLoginIframe: false,
        silentCheckSsoRedirectUri: window.location.origin + '/assets/silent-check-sso.html'
      },
      bearerExcludedUrls: ['/assets', '/bdomobile.json']
    });
}

export function packagingApiConfigFactory(): PackagingConfiguration {
  const params: PackagingConfigurationParameters = {
    basePath: `${environment.apiUrl}pms`,
  };
  return new PackagingConfiguration(params);
}

export function wasteRecordApiConfigFactory(): WasteRecordConfiguration {
  const params: WasteRecordConfigurationParameters = {
    basePath: `${environment.apiUrl}wasterecord`,
    encodeParam: encodeApiParam,
  };
  return new WasteRecordConfiguration(params);
}

export function authApiConfigFactory(keycloak: KeycloakService): AuthServiceConfiguration {
  return new AuthServiceConfiguration({
    basePath: `${environment.apiUrl}uaa`,
    credentials: {
      'bearer-jwt': () => keycloak.getKeycloakInstance()?.token ?? '',
    },
  });
}

export function authApiConfigFactoryForRoot(): AuthServiceConfiguration {
  return new AuthServiceConfiguration({
    basePath: `${environment.apiUrl}uaa`,
  });
}

export function accountApiConfigFactory(): AccountServiceConfiguration {
  const params: AccountServiceConfigurationParameters = {
    basePath: `${environment.apiUrl}accounts`,
  };
  return new AccountServiceConfiguration(params);
}

export function reportingApiConfigFactory(): ReportingServiceConfiguration {
  const params: ReportingServiceConfigurationParameters = {
    basePath: `${environment.apiUrl}rs`,
    encodeParam: encodeApiParam,
  };
  return new ReportingServiceConfiguration(params);
}

@NgModule({
  declarations: [
    AppComponent,
  ],
  bootstrap: [AppComponent], imports: [BrowserModule,
    AppRoutingModule,
    HttpClientModule,
    CommonModule,
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    FormsModule,
    KeycloakAngularModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: !isDevMode(),
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000'
    }),
    FontAwesomeModule,
    TranslateModule.forRoot({
      // Without a fallback, ngx-translate renders the raw key - users saw literal
      // `wasteRegister.activationDialog.scanResumeDateHelp.withHistory` on screen. Polish is
      // the fallback because it is the only complete file: en still trails it by 303 keys.
      fallbackLang: 'pl',
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      }
    }),
    GoogleTagManagerModule,
    MessageModule,
    ToastModule,
    DashboardModule,
    Error404Module,
    StoreModule.forRoot({
      companyContext: companyContextReducer,
    }, {
      metaReducers: [
        localStorageMetaReducer,
      ]
    }),
    EffectsModule.forRoot([]),
    StoreDevtoolsModule.instrument({ maxAge: 25, logOnly: !isDevMode() }),
    PackagingApiModule.forRoot(packagingApiConfigFactory),
    WasteRecordApiModule.forRoot(wasteRecordApiConfigFactory),
    AuthServiceApiModule.forRoot(authApiConfigFactoryForRoot),
    AccountServiceApiModule.forRoot(accountApiConfigFactory),
    ReportingServiceApiModule.forRoot(reportingApiConfigFactory),
  ],
  providers: [
    DialogService,
    {
      provide: LOCALE_ID,
      useValue: 'pl-PL'
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeKeycloak,
      multi: true,
      deps: [KeycloakService]
    },
    {
      provide: 'googleTagManagerId',
      useValue: environment.googleTagManagerId
    },
    { provide: 'googleTagManagerPreview', useValue: environment.googleTagManagerPreview },
    { provide: 'googleTagManagerAuth', useValue: environment.googleTagManagerAuth },
    { provide: HTTP_INTERCEPTORS, useClass: KeycloakBearerInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: DateInterceptor, multi: true },
    {
      provide: AuthServiceConfiguration,
      useFactory: authApiConfigFactory,
      deps: [KeycloakService],
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    }, {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {  /* empty */ },
      deps: [Sentry.TraceService],
      multi: true,
    },
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimationsAsync(),
    provideEkoHostAppMetadata({
      name: packageJson.name,
      version: packageJson.version,
    }),
    providePrimeNG({
      theme: {
        preset: ekowitrynaPreset,
        options: {
          darkModeSelector: '.dark',
          cssLayer: false,
        }
      }
    }),
    ...provideEkoPreferencesSync(),
    provideEkoBrowserTitle('app.browserTitle'),
  ],
})
export class AppModule { }
